import type {
    DeviceGroupPayload,
    DeviceGroupResponse,
} from '@/types/apps/device-group.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type DeviceGroupListResponse = ApiResponse<
    PaginatedResult<DeviceGroupResponse>
>;

export class DeviceGroupService
    implements Indexable, Showable, Creatable, Updatable, Deletable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/device-group',
        detail: (id: string) => `/api/device-group/${id}`,
        import: () => '/api/device-group/import',
        export: () => '/api/device-group/export',
    };

    public index(params?: QueryParams): Promise<DeviceGroupListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<DeviceGroupResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: DeviceGroupPayload
    ): Promise<ApiResponse<DeviceGroupResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: DeviceGroupPayload
    ): Promise<ApiResponse<DeviceGroupResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.putApi(url, payload);
    }

    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }
}
